import { FC, useContext, useEffect, useState } from 'react'
import { Control, Controller, useFormContext } from 'react-hook-form'

// Components
import { Autocomplete, Box, FormHelperText, TextField } from '@mui/material'

// Others
import { UserRole, User, EntryType, FetchBillDetailsQuery } from '../../../../generated'
import { DEFAULT_USER_ROLES } from '../../../constants'
import { AuthContext } from '../../../context'
import { EditBillForm } from '../../../interfaceTypes/forms'

type Case = FetchBillDetailsQuery['fetchBillDetails']['bill']['case']
type CaseUsers = NonNullable<Case>['caseToUsers']

interface UserDropdownProps {
  name: `${keyof Omit<EditBillForm, 'discountPercentage' | 'isPrePaid'>}.${number}.userId`
  caseUsers?: CaseUsers | null
  control: Control<EditBillForm, any>
  entryType: EntryType
  disabled?: boolean
}

const filterUsersByAllowedRoles = (caseUsers: CaseUsers, allowedRoles: UserRole[]) => {
  const users: Partial<User>[] = []
  caseUsers?.forEach(caseUser => {
    if (
      caseUser?.user &&
      caseUser.user.roles?.some(userRole => userRole?.role && allowedRoles.includes(userRole.role))
    ) {
      users.push({
        id: caseUser.user.id,
        firstName: caseUser.user.firstName || '',
        lastName: caseUser.user.lastName || '',
        hourlyRate: caseUser.hourlyRate ?? 0
      })
    }
  })

  const uniqueUsers = Array.from(new Map(users.map(user => [user.id, user])).values())
  return uniqueUsers
}

const getAllowedRoles = (entryType: EntryType): UserRole[] => {
  switch (entryType) {
    case EntryType.Cost:
      return [UserRole.Investigator]
    case EntryType.Time:
      return [UserRole.Attorney, UserRole.Paralegal, UserRole.Admin]
    default:
      return DEFAULT_USER_ROLES
  }
}

const UserDropdown: FC<UserDropdownProps> = ({ name, control, entryType, caseUsers, disabled }) => {
  const { currentUser, isAdmin, isParalegal, isAttorney } = useContext(AuthContext)
  const { setValue } = useFormContext<EditBillForm>()
  const [options, setOptions] = useState<Partial<User>[]>([])

  const isCostEntry = entryType === EntryType.Cost

  useEffect(() => {
    const users: Partial<User>[] = []
    const allowedRoles = getAllowedRoles(entryType)

    if (!currentUser?.id) return

    if (allowedRoles.includes(UserRole.Admin)) users.push(currentUser)

    users.push(...filterUsersByAllowedRoles(caseUsers, allowedRoles))

    setOptions(users)
    return

    // Commented out for now to allow all case users to be selected
    // Otherwise, paralegls were not able to add entries on behalf of attorneys or investigators

    // if (isParalegal) {
    //   users.push(currentUser)

    //   if (currentUser?.supervisingUsers?.length) {
    //     users.push(...currentUser.supervisingUsers.filter((user): user is User => user !== null))
    //   }

    //   setOptions(users)
    //   return
    // }

    // if (isAdmin) {
    //   if (allowedRoles.includes(UserRole.Admin)) users.push(currentUser)

    //   users.push(...filterUsersByAllowedRoles(caseUsers, allowedRoles))

    //   setOptions(users)
    //   return
    // }

    // if (isAttorney) {
    //   users.push(currentUser)
    //   setOptions(users)
    //   return
    // }
  }, [caseUsers, currentUser, entryType, isAdmin, isParalegal, isAttorney])

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error: { message } = {}, invalid } }) => {
        const value = options.find(user => user?.id === field.value) || null

        return (
          <Box>
            <Autocomplete
              {...field}
              disabled={disabled}
              options={options}
              value={value}
              onChange={(_, newValue) => {
                field.onChange(newValue?.id || '')
                if (newValue) {
                  const rateName = name.replace(/\.userId$/, '.rate') as Parameters<typeof setValue>[0]
                  setValue(rateName, newValue.hourlyRate ?? 0)
                }
              }}
              getOptionLabel={option => `${option?.firstName || ''} ${option?.lastName || ''}`.trim()}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderInput={params => (
                <TextField {...params} label={isCostEntry ? 'Investigator' : 'User'} error={!!invalid} />
              )}
            />
            {invalid && message && (
              <FormHelperText sx={{ color: theme => theme.palette.error.main }}>{message}</FormHelperText>
            )}
          </Box>
        )
      }}
    />
  )
}

export default UserDropdown
